import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Shield, Zap, BarChart3, Brain, Wrench } from 'lucide-react';
import { products } from '../mockData';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const Rental = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  
  const benefits = {
    finance: [
      {
        icon: <TrendingUp className="w-6 h-6" />,
        titleKey: 'financeFixed',
        descKey: 'financeFixedDesc'
      },
      {
        icon: <Shield className="w-6 h-6" />,
        titleKey: 'financeNoCap',
        descKey: 'financeNoCapDesc'
      },
      {
        icon: <CheckCircle className="w-6 h-6" />,
        titleKey: 'financePredictable',
        descKey: 'financePredictableDesc'
      }
    ],
    operation: [
      {
        icon: <Zap className="w-6 h-6" />,
        titleKey: 'operationContinuity',
        descKey: 'operationContinuityDesc'
      },
      {
        icon: <Brain className="w-6 h-6" />,
        titleKey: 'operationAI',
        descKey: 'operationAIDesc'
      },
      {
        icon: <Wrench className="w-6 h-6" />,
        titleKey: 'operationMaintenance',
        descKey: 'operationMaintenanceDesc'
      }
    ],
    procurement: [
      {
        icon: <CheckCircle className="w-6 h-6" />,
        titleKey: 'procurementSingle',
        descKey: 'procurementSingleDesc'
      },
      {
        icon: <Shield className="w-6 h-6" />,
        titleKey: 'procurementSimple',
        descKey: 'procurementSimpleDesc'
      }
    ],
    management: [
      {
        icon: <BarChart3 className="w-6 h-6" />,
        titleKey: 'managementPerformance',
        descKey: 'managementPerformanceDesc'
      },
      {
        icon: <Shield className="w-6 h-6" />,
        titleKey: 'managementRisk',
        descKey: 'managementRiskDesc'
      }
    ]
  };

  const comparison = [
    { criteriaKey: 'comparisonInvestment', classicKey: 'comparisonClassicInv', aiKey: 'comparisonAIInv' },
    { criteriaKey: 'comparisonCashFlow', classicKey: 'comparisonClassicCash', aiKey: 'comparisonAICash' },
    { criteriaKey: 'comparisonCost', classicKey: 'comparisonClassicCost', aiKey: 'comparisonAICost' },
    { criteriaKey: 'comparisonMaintenance', classicKey: 'comparisonClassicMain', aiKey: 'comparisonAIMain' },
    { criteriaKey: 'comparisonControl', classicKey: 'comparisonClassicControl', aiKey: 'comparisonAIControl' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_multi-lingua-3/artifacts/ql7zbvyi_operasyonel%20kiralama%20resim.png"
            alt={t('rentalTitle')}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 70%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 via-sky-800/80 to-transparent"></div>
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white max-w-2xl">
            <div className="inline-block bg-sky-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('aiAssisted')}
            </div>
            <h1 className="text-6xl font-bold mb-4">{t('rentalTitle')}</h1>
            <p className="text-2xl mb-2 font-light">{t('rentalSubtitle')}</p>
            <p className="text-xl text-sky-100 italic">"{t('rentalQuote')}"</p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('operationBenefits')}</h2>
            <p className="text-xl text-gray-600">{t('operationBenefitsDesc')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Finance */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('financeTitle')}</h3>
              <div className="space-y-4">
                {benefits.finance.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mr-3 mt-1">{item.icon}</div>
                    <div>
                      <h4 className="font-semibold">{t(item.titleKey)}</h4>
                      <p className="text-sm text-purple-100">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Operation */}
            <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-6 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('operationTitle')}</h3>
              <div className="space-y-4">
                {benefits.operation.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mr-3 mt-1">{item.icon}</div>
                    <div>
                      <h4 className="font-semibold">{t(item.titleKey)}</h4>
                      <p className="text-sm text-pink-100">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Procurement */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('procurementTitle')}</h3>
              <div className="space-y-4">
                {benefits.procurement.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mr-3 mt-1">{item.icon}</div>
                    <div>
                      <h4 className="font-semibold">{t(item.titleKey)}</h4>
                      <p className="text-sm text-blue-100">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Management */}
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
              <h3 className="text-2xl font-bold mb-4">{t('managementTitle')}</h3>
              <div className="space-y-4">
                {benefits.management.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mr-3 mt-1">{item.icon}</div>
                    <div>
                      <h4 className="font-semibold">{t(item.titleKey)}</h4>
                      <p className="text-sm text-teal-100">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('comparisonTitle')}</h2>
            <p className="text-xl text-gray-600">{t('comparisonDesc')}</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white shadow-xl rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="py-4 px-6 text-left">{t('comparisonCriteria')}</th>
                  <th className="py-4 px-6 text-left bg-gray-700">{t('comparisonClassic')}</th>
                  <th className="py-4 px-6 text-left bg-sky-600">{t('comparisonAI')}</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-4 px-6 font-semibold text-gray-900">{t(row.criteriaKey)}</td>
                    <td className="py-4 px-6 text-gray-600">{t(row.classicKey)}</td>
                    <td className="py-4 px-6 text-sky-900 font-medium bg-sky-50">{t(row.aiKey)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Product Catalog */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t('availableProducts')}</h2>
            <p className="text-xl text-gray-600">{t('rentalProductsDesc')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.professional.slice(0, 6).map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={t(product.nameKey)}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{t(product.nameKey)}</h3>
                  <p className="text-gray-600 mb-4">{t(product.descriptionKey)}</p>
                  <div className="flex space-x-2">
                    <Link to={product.link} className="flex-1">
                      <Button variant="outline" className="w-full">
                        {t('view')}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to="/contact" className="flex-1">
                      <Button className="w-full bg-sky-500 hover:bg-sky-600">
                        {t('requestQuote')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sky-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">{t('rentalCTA')}</h2>
          <p className="text-xl mb-8">{t('rentalCTADesc')}</p>
          <Link to="/contact">
            <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg">
              {t('contact')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Rental;
